import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  BackfillBundleRequestSchema,
  BackfillBundleResponseSchema,
  BackfillCandidatesQuerySchema,
  BackfillCandidatesResponseSchema,
  ErrorGenericSchema,
} from '../models';

extendZodWithOpenApi(z);

const c = initContract();

/**
 * INTERNAL-ONLY TIMESHEET-PROPOSAL BACKFILL CONTRACT
 *
 * The two ops here are bulk service-role reads over proposal notes/items/logs
 * and account/user IDs — a materially larger PII surface than the single-day
 * reads — so they are reachable ONLY on the internal `/api` mount. This router
 * is referenced solely by the internal `internalApiSpec` in
 * `apps/integration-cloud/src/internal/api/index.ts`; it is deliberately NOT
 * assembled into the public `apiSpec` (`src/api.ts`), so the public app never
 * routes these paths (proved by a public-app 404 negative test).
 *
 * Auth is handler-local (shared-secret `INTERNAL_TIMELY_WEBHOOK_API_KEY` via
 * `apiRouteInitializer`), identical to the existing internal webhooks — no
 * Google admin session.
 */
export const internalApiTimesheetProposals = c.router({
  /**
   * Keyset enumeration of deduplicated backfill candidates. Exactly one
   * survivor per (timely_account_id, timely_user_id, date) — chosen
   * accepted-first at the source via a window function BEFORE keyset paging —
   * so fill-empty-only never imports a pending row over an accepted one.
   */
  backfillCandidates: {
    method: 'GET',
    // 4-segment path (`backfill/candidates`) so it cannot be shadowed on the
    // public app by `GET /1.0/timesheet-proposals/:id` (2-segment) — the public
    // app cleanly 404s it. (The op is internal-only regardless; this keeps the
    // public negative test a clean 404.)
    path: `/1.0/timesheet-proposals/backfill/candidates`,
    description:
      'INTERNAL: keyset enumeration of deduplicated backfill candidates (one accepted-first survivor per account/user/date). Service-role only.',
    summary: 'Enumerate backfill candidates (internal)',
    query: BackfillCandidatesQuerySchema,
    responses: {
      200: BackfillCandidatesResponseSchema,
      400: ErrorGenericSchema,
      500: ErrorGenericSchema,
    },
  },

  /**
   * Batch full-bundle fetch. Returns proposal head + entries[] + items[] for
   * each found id (service-role, cross-user), and echoes the ids that had no
   * matching row as `not_found`.
   */
  backfillBundle: {
    method: 'POST',
    path: `/1.0/timesheet-proposals/backfill/bundle`,
    description:
      'INTERNAL: batch fetch of full proposal bundles by id (service-role, cross-user). Echoes unmatched ids as not_found.',
    summary: 'Batch fetch backfill bundles (internal)',
    body: BackfillBundleRequestSchema,
    responses: {
      200: BackfillBundleResponseSchema,
      400: ErrorGenericSchema,
      500: ErrorGenericSchema,
    },
  },
});
